/**
 * Base API client using the native fetch API.
 * - Injects the JWT access token on every request
 * - On 401: attempts a silent token refresh, then retries once
 * - On refresh failure: clears tokens and redirects to /login
 */

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ── Token helpers ────────────────────────────────────────────────────────────

export const TokenStore = {
  getAccess:  ()      => localStorage.getItem('access_token'),
  getRefresh: ()      => localStorage.getItem('refresh_token'),
  set:        (a, r)  => {
    localStorage.setItem('access_token',  a);
    localStorage.setItem('refresh_token', r);
  },
  clear: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },
};

// ── Silent refresh ───────────────────────────────────────────────────────────

async function refreshAccessToken() {
  const refresh = TokenStore.getRefresh();
  if (!refresh) throw new Error('No refresh token');

  const res = await fetch(`${BASE_URL}/auth/refresh/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ refresh }),
  });

  if (!res.ok) throw new Error('Refresh failed');

  const data = await res.json();
  TokenStore.set(data.access, data.refresh ?? refresh);
  return data.access;
}

// ── Core request ─────────────────────────────────────────────────────────────

async function request(path, options = {}, retry = true) {
  const { body, ...rest } = options;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const access = TokenStore.getAccess();
  if (access) headers['Authorization'] = `Bearer ${access}`;

  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Silent refresh on 401
  if (res.status === 401 && retry) {
    try {
      await refreshAccessToken();
      return request(path, options, false);
    } catch {
      TokenStore.clear();
      window.location.href = '/login';
      return;
    }
  }

  // No content
  if (res.status === 204) return null;

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const error = new Error(data?.detail || `Request failed: ${res.status}`);
    error.status = res.status;
    error.data   = data;
    throw error;
  }

  return data;
}

// ── Convenience methods ───────────────────────────────────────────────────────

const client = {
  get:    (path, opts)         => request(path, { method: 'GET',    ...opts }),
  post:   (path, body, opts)   => request(path, { method: 'POST',   body, ...opts }),
  put:    (path, body, opts)   => request(path, { method: 'PUT',    body, ...opts }),
  patch:  (path, body, opts)   => request(path, { method: 'PATCH',  body, ...opts }),
  delete: (path, opts)         => request(path, { method: 'DELETE', ...opts }),
};

export default client;
